import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Eye, Search } from "lucide-react";
import { useT52 } from "@/i18n/v52";
import { Dash, Empty52, PlanChip, QaTag } from "@/components/v52/Bits";

const nameOf = (o) => o.company_name || o.tenant_id;

function OrgRow({ o, isSuper, onCockpit }) {
  const { t52 } = useT52();
  const navigate = useNavigate();
  const to = `/brockis/${o.tenant_id}`;
  return (
    <tr data-testid={`org-row-${o.tenant_id}`} onClick={(e) => { if (!e.target.closest("a, button")) navigate(to); }}>
      <td><Link className="f52-firm" to={to} data-testid={`org-link-${o.tenant_id}`}>{nameOf(o)}</Link>{o.is_demo && <QaTag />}</td>
      <td className="f52-tid">{o.tenant_id}</td>
      <td><Dash v={o.city} /></td>
      <td><PlanChip plan={o.plan} /></td>
      <td><Dash v={o.status} /></td>
      <td className="num"><Dash v={o.membership_count} /></td>
      <td className="num"><Dash v={o.item_count} /></td>
      <td className="num"><Dash v={o.inquiry_count} /></td>
      <td>
        <div className="f52-row-actions">
          {isSuper && (
            <button type="button" className="f52-btn f52-btn-secondary" onClick={() => onCockpit(o.tenant_id)} data-testid={`org-cockpit-btn-${o.tenant_id}`}>
              <Eye aria-hidden="true" /> Cockpit
            </button>
          )}
          <Link className="f52-icon-link" to={to} aria-label={t52("k_openAria", { name: nameOf(o) })}><ChevronRight aria-hidden="true" /></Link>
        </div>
      </td>
    </tr>
  );
}

function OrgCard({ o, isSuper, onCockpit }) {
  const { t52 } = useT52();
  const to = `/brockis/${o.tenant_id}`;
  const f = [["k_thCity", o.city], ["k_thStatus", o.status], ["k_members", o.membership_count], ["k_thItems", o.item_count], ["k_thInq", o.inquiry_count]];
  return (
    <li className="f52-ccard" data-testid={`org-card-${o.tenant_id}`}>
      <div className="ch">
        <div><p className="cn">{nameOf(o)}{o.is_demo && <QaTag />}</p><p className="f52-tid">{o.tenant_id}</p></div>
        <PlanChip plan={o.plan} />
      </div>
      <dl className="f52-cdl">{f.map(([k, v]) => <div key={k}><dt>{t52(k)}</dt><dd><Dash v={v} /></dd></div>)}</dl>
      <div className="f52-cact">
        <Link className="f52-btn f52-btn-primary" to={to} data-testid={`org-card-open-${o.tenant_id}`}>{t52("k_open")}</Link>
        {isSuper && (
          <button type="button" className="f52-btn f52-btn-secondary" onClick={() => onCockpit(o.tenant_id)} data-testid={`org-card-cockpit-${o.tenant_id}`}>
            <Eye aria-hidden="true" /> Cockpit
          </button>
        )}
      </div>
    </li>
  );
}

function OrgRows({ rows, isSuper, onCockpit, testid }) {
  const { t52 } = useT52();
  const heads = ["k_thFirm", "k_thTid", "k_thCity", "k_thPlan", "k_thStatus"];
  return (
    <>
      <div className="f52-tbl-wrap">
        <table className="f52-tbl" data-testid={`${testid}-table`}>
          <thead>
            <tr>
              {heads.map((h) => <th key={h} scope="col">{t52(h)}</th>)}
              <th scope="col" className="num">{t52("k_thMembers")}</th><th scope="col" className="num">{t52("k_thItems")}</th>
              <th scope="col" className="num">{t52("k_thInq")}</th><th scope="col" className="act-col"><span className="f52-sr">{t52("k_actions")}</span></th>
            </tr>
          </thead>
          <tbody>{rows.map((o) => <OrgRow key={o.tenant_id} o={o} isSuper={isSuper} onCockpit={onCockpit} />)}</tbody>
        </table>
      </div>
      <ul className="f52-cards" data-testid={`${testid}-cards`}>{rows.map((o) => <OrgCard key={o.tenant_id} o={o} isSuper={isSuper} onCockpit={onCockpit} />)}</ul>
    </>
  );
}

// Panel mit Liste, Trefferzahl und Leer-/Kein-Treffer-Zustand.
export function OrgSection({ title, chip, rows, total, q, onReset, isSuper, onCockpit, testid, empty }) {
  const { t52 } = useT52();
  const count = q ? t52("k_of", { a: rows.length, b: total }) : total;
  return (
    <section className="f52-panel" aria-labelledby={`${testid}-h`} data-testid={testid}>
      <div className="f52-panel-head">
        <h2 id={`${testid}-h`}>{title} <span className="f52-count" aria-live="polite" data-testid={`${testid}-count`}>({count})</span></h2>
        {chip}
      </div>
      {rows.length ? <OrgRows rows={rows} isSuper={isSuper} onCockpit={onCockpit} testid={testid} />
        : q && total ? (
          <Empty52 icon={Search} tone="neutral" title={t52("k_nohitT", { q })} text={t52("k_nohitD")} testid={`${testid}-nohit`}>
            <button type="button" className="f52-btn f52-btn-secondary" onClick={onReset} data-testid={`${testid}-search-reset`}>{t52("k_reset")}</button>
          </Empty52>
        ) : empty}
    </section>
  );
}
